import { Modal } from "@ths/ui";
import { useEffect, useRef, useState, type RefObject } from "react";

import {
  useCheckoutReminderOptionsQuery,
  useCheckoutReminderPreferencesQuery,
  useUpdateCheckoutReminderPreferencesMutation,
} from "../generated/graphql";

import type { ReminderOptions } from "./ReminderConsentFields";
import {
  ReminderPreferenceForm,
  type ReminderPreference,
} from "./ReminderPreferenceForm";

// A local fallback preserves dismissal if the connection drops while saving it.
// The server also records the guest's response across stays, devices, and sessions.
const dismissalKey = (guestId: string) =>
  `ths-basecamp-reminder-dismissed:guest:${guestId}`;
function locallyDismissed(guestId: string) {
  try {
    return localStorage.getItem(dismissalKey(guestId)) === "true";
  } catch {
    return false;
  }
}
function rememberDismissal(guestId: string) {
  try {
    localStorage.setItem(dismissalKey(guestId), "true");
  } catch {
    /* The authenticated server response remains authoritative. */
  }
}

type PreferenceProps = {
  token: string;
  assignmentId: string;
  mode: "prompt" | "settings";
  returnFocusRef?: RefObject<HTMLButtonElement>;
};

function PreferenceView({
  token,
  assignmentId,
  mode,
  returnFocusRef,
  current,
  options,
  onSaved,
}: PreferenceProps & {
  current: ReminderPreference;
  options: ReminderOptions;
  onSaved: () => Promise<unknown>;
}) {
  const [save] = useUpdateCheckoutReminderPreferencesMutation();
  const [dismissed, setDismissed] = useState(() =>
    locallyDismissed(current.guestId),
  );
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [requirePhone, setRequirePhone] = useState(false);
  const wasOpen = useRef(false);
  const open =
    mode === "prompt" &&
    options.smsAvailable &&
    !current.smsEnabled &&
    !current.smsBlockedReason &&
    !current.promptDismissed &&
    !dismissed &&
    !completed;

  // Retry a locally remembered dismissal on the next visit if the previous write failed.
  useEffect(() => {
    if (!dismissed || current.promptDismissed) return;
    void save({
      variables: { input: { token, assignmentId, dismissPrompt: true } },
    }).catch(() => {
      /* Keep the local fallback; do not block access to the stay. */
    });
  }, [dismissed, current.promptDismissed, save, token, assignmentId]);

  useEffect(() => {
    if (!open && wasOpen.current) returnFocusRef?.current?.focus();
    wasOpen.current = open;
  }, [open, returnFocusRef]);

  const dismiss = () => {
    if (saving) return;
    rememberDismissal(current.guestId);
    setDismissed(true);
  };
  const form = (
    <ReminderPreferenceForm
      current={current}
      options={options}
      prompt={mode === "prompt"}
      requirePhone={requirePhone}
      onDismiss={dismiss}
      onSavingChange={setSaving}
      onSave={async (sms, email, phone) => {
        try {
          await save({
            variables: {
              input: {
                token,
                assignmentId,
                ...(mode === "settings" &&
                options.emailAvailable &&
                !current.emailBlockedReason
                  ? { emailEnabled: email }
                  : {}),
                ...(sms != null &&
                options.smsAvailable &&
                !current.smsBlockedReason
                  ? {
                      smsEnabled: sms,
                      smsDisclosureVersion: options.disclosureVersion,
                      ...(phone ? { phone } : {}),
                    }
                  : {}),
              },
            },
          });
          rememberDismissal(current.guestId);
          if (mode === "prompt") setCompleted(true);
          await onSaved();
        } catch (error) {
          const detail = error instanceof Error ? error.message : "";
          if (detail.includes("valid US mobile number")) setRequirePhone(true);
          const publicMessages = [
            "Texts to this number are stopped. Please contact Basecamp staff for help.",
            "Emails to this address are stopped. Please contact Basecamp staff for help.",
          ];
          throw new Error(
            detail.includes("valid US mobile number")
              ? "Text reminders need a valid US mobile number. Check your number and try again."
              : publicMessages.includes(detail)
                ? detail
                : "We could not save your preferences. Please try again.",
          );
        }
      }}
    />
  );
  if (mode === "settings") return form;
  return (
    <Modal
      open={open}
      onOpenChange={(value) => {
        if (!value) dismiss();
      }}
      title="Lodging updates"
      description="Get texts about your reservations and stays."
      className="max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border border-on-navy/20 bg-navy-strong text-on-navy [&_button[aria-label=Close]]:text-on-navy/70 [&_h2]:text-on-navy [&_p.text-text-secondary]:text-on-navy/70"
    >
      <div className="py-3">{form}</div>
    </Modal>
  );
}

export function CheckoutReminderPreferences(props: PreferenceProps) {
  const { token, assignmentId, mode } = props;
  const options = useCheckoutReminderOptionsQuery();
  const { data, error, loading, refetch } = useCheckoutReminderPreferencesQuery(
    {
      variables: { token, assignmentId },
      fetchPolicy: "network-only",
      skip: !options.data?.checkoutReminderOptions.enabled,
    },
  );
  const config = options.data?.checkoutReminderOptions;
  if (!config?.enabled || (!config.smsAvailable && !config.emailAvailable))
    return null;
  if (loading)
    return mode === "prompt" ? null : (
      <p role="status" className="text-sm text-on-navy/70">
        Loading reminders…
      </p>
    );
  if (error || !data)
    return mode === "prompt" ? null : (
      <p role="status" className="text-sm text-on-navy/70">
        Reminders are unavailable. Try again when connected.
      </p>
    );
  return (
    <PreferenceView
      key={`${data.checkoutReminderPreferences.guestId}:${assignmentId}:${mode}`}
      {...props}
      current={data.checkoutReminderPreferences}
      options={config}
      onSaved={refetch}
    />
  );
}
